import { forwardRef, useMemo, useState } from 'react'
import styled from 'styled-components'
import Icon from '@/components/common/Icon'
import Text from '@/components/common/Text'
import { device } from '@/common/constants/device'

const InputContainer = styled.input.attrs((props) => ({
  className: props.theme ?? '',
}))`
  border: 1px solid var(--${(props) => (props.isError ? 'red' : 'divider')});
  border-radius: ${(props) => (props.br ? props.br : '3px')};
  width: ${(props) => (props.width ? `${props.width}px` : '100%')};
  color: var(--black);
  font-size: 16px;
  padding: ${(props) =>
    props.startIcon ? `16px 20px 16px 48px` : '16px 20px'};

  @media ${device.laptopL} {
    padding: ${(props) =>
      props.startIcon ? `16px 20px 16px 48px` : '20px 24px'};
  }

  ::placeholder {
    color: ${({ placeholderColor }) =>
      placeholderColor ? `var(--${placeholderColor})` : ''};
  }

  :focus,
  :hover {
    box-shadow: 0px 0px 0px 2px
      ${(props) => (props.isError ? 'rgba(242, 29, 68, 0.2)' : '#d4dced')};
  }

  &.dark {
    border: 1px solid var(--${(props) => (props.isError ? 'red' : 'dark-grey')});

    &:hover {
      box-shadow: none;
      border-color: #bebebf;
    }

    &:focus {
      box-shadow: 0px 0px 0px 4px
        ${(props) =>
          props.isError ? 'rgba(242, 29, 68, 0.2)' : `var(--dark-grey)`};
      border-color: transparent;
    }
  }

  &.grey {
    border: 1px solid var(--${(props) => (props.isError ? 'red' : 'stroke')});
    border-radius: 12px;

    &:hover {
      box-shadow: none;
      border-color: #bebebf;
    }

    &:focus {
      box-shadow: 0px 0px 0px 4px
        ${(props) =>
          props.isError ? 'rgba(242, 29, 68, 0.2)' : `var(--stroke)`};
      border-color: transparent;
    }
  }
`

const EyeIcon = styled(Icon)`
  position: absolute;
  top: 14px;
  right: 16px;
  cursor: pointer;
  z-index: 1;
  width: 24px;
  height: 24px;

  @media ${device.laptopL} {
    top: 18px;
  }

  &:hover {
    background-color: var(--black);
  }

  &:active,
  &:focus {
    background-color: var(--black);
  }
`

const StartIcon = styled.div`
  position: absolute;
  top: 13px;
  left: 16px;
  width: 24px;
  height: 24px;
`

const Tooltip = styled.div`
  margin-top: 0.25rem;
`

const InputWrapper = styled.div`
  position: relative;
  transition: opacity 0.26s ease-in-out;

  & ${InputContainer}:hover + ${EyeIcon} {
    background-color: var(--black);
  }

  &.disabled {
    opacity: 0.7;
    pointer-events: none;
  }
`

const TextCointainer = styled(Text)`
  max-width: 345px;
  text-align: left;
`

const Input = forwardRef(
  (
    {
      type,
      readOnly,
      tooltipMessage,
      className,
      id,
      br,
      theme,
      startIcon,
      ...props
    },
    ref
  ) => {
    const [isContentVisible, setIsContentVisible] = useState(false)

    const isPasswordType = useMemo(() => type === 'password', [type])
    const isEmailType = useMemo(() => type === 'email', [type])

    const inputType = useMemo(() => {
      if (!isContentVisible && isPasswordType) {
        return 'password'
      } else if (isEmailType) {
        return 'email'
      } else {
        return 'text'
      }
    }, [isContentVisible, isPasswordType, isEmailType])

    const togglePasswordVisibility = () => {
      setIsContentVisible(!isContentVisible)
    }

    return (
      <InputWrapper className={className} data-tip data-for={id}>
        {startIcon && <StartIcon className="input-icon">{startIcon}</StartIcon>}
        <InputContainer
          br={br}
          {...props}
          ref={ref}
          startIcon={startIcon}
          type={inputType}
          theme={theme}
          readOnly={readOnly}
        />
        {!!tooltipMessage && (
          <Tooltip
            data-cy={`tooltip-${id}`}
            place="bottom"
            id={id}
            arrowColor="white"
            effect="solid"
          >
            <TextCointainer size="14" color="red">
              {tooltipMessage}
            </TextCointainer>
          </Tooltip>
        )}
        {isPasswordType && (
          <EyeIcon
            icon={isContentVisible ? 'open-eye' : 'hide-eye'}
            color="grey"
            onClick={togglePasswordVisibility}
          />
        )}
      </InputWrapper>
    )
  }
)
Input.displayName = 'Input'
export default Input
